import React from 'react';
import {useDispatch} from 'react-redux';
import {WeatherService} from '../api';
import {StationRecords} from '../api/types';
import {
  EMPTY_STATION_RECORDS,
  getStationRecordsData,
  pushStationRecords,
} from '../store';
import {Dates} from '../utils';
import useApiClient from './useApiClient';
import useParamSelector from './useParamSelector';

// Records/climatology change at most once a day (nightly profile recompute).
const STALENESS_MIN = 6 * 60;
const RETRY_COOLDOWN_MIN = 5;

const useStationRecords = (stationId: number) => {
  const {fetch} = useApiClient();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = React.useState(false);
  const lastAttemptAtRef = React.useRef<number | undefined>(undefined);

  const recordsData: StationRecords = useParamSelector(
    getStationRecordsData,
    stationId,
  );

  const isUpdateRequired = React.useCallback(() => {
    const isStale =
      !recordsData ||
      recordsData === EMPTY_STATION_RECORDS ||
      !recordsData.fetched_at ||
      Dates.timeDiffUtcWithNowInMin(recordsData.fetched_at) > STALENESS_MIN;
    if (!isStale) {
      return false;
    }
    if (
      lastAttemptAtRef.current !== undefined &&
      Dates.timeDiffUtcWithNowInMin(lastAttemptAtRef.current) <
        RETRY_COOLDOWN_MIN
    ) {
      return false;
    }
    return true;
  }, [recordsData]);

  const updateRecords = React.useCallback(async () => {
    if (isLoading || !stationId) {
      return;
    }

    lastAttemptAtRef.current = Dates.now();
    setIsLoading(true);
    try {
      const data: StationRecords = await fetch(
        WeatherService.stationRecords(stationId),
        {ignore404: true},
      );
      // A 204 (records not built yet) yields no payload — cache the empty
      // marker with a timestamp so we do not hammer the endpoint.
      dispatch(
        pushStationRecords({
          stationId,
          data: {...(data || {}), fetched_at: Dates.now()},
        }),
      );
    } catch (_error) {
      // Cooldown via lastAttemptAtRef prevents a hot retry loop on persistent failure.
    } finally {
      setIsLoading(false);
    }
  }, [fetch, stationId, dispatch, isLoading]);

  const memoizedReturn = React.useMemo(
    () => ({
      recordsData,
      isLoading,
      isUpdateRequired,
      updateRecords,
    }),
    [recordsData, isLoading, isUpdateRequired, updateRecords],
  );

  return memoizedReturn;
};

export default useStationRecords;
